import React from "react";
import ChartHeader from "../shared/controls/ChartHeader";
import D3AreaChart from "../shared/rendering/D3AreaChart";
import ResponsiveD3Container from "../shared/rendering/ResponsiveD3Container";
import ExpandedChartLayout from "../shared/layout/ExpandedChartLayout";
import BaseExpandableChart from "../shared/base/BaseExpandableChart";
import { getChartDims } from "../../../lib/utils";
import { CHART_MARGIN, EXPANDED_CHART_MARGIN } from "../../../lib/constants";
import styles from "../analysis/Charts.module.css";
import chartStyles from "../../../styles/components/charts.module.css";

type MobileTab = string | undefined;

export interface TimelineChartProps {
  data: Array<any>;
  previewData?: Array<any>;
  previewHideFinalYear?: boolean;
  showVolume?: boolean;
  title?: string;
  yTickCount?: number | null;
  yTickValues?: Array<number> | null;
  mobileShowOnly?: MobileTab;
}

export const TimelineChart: React.FC<TimelineChartProps> = ({
  data,
  previewData,
  previewHideFinalYear,
  showVolume = false,
  title,
  yTickCount = null,
  yTickValues = null,
  mobileShowOnly,
}) => {
  const chartKey = showVolume ? "volume" : "count";
  const headerTitle = title || (showVolume ? "Invested capital" : "Number of deals");
  const chartColor = "#E84A5F";

  const dims = getChartDims(false, undefined, CHART_MARGIN);
  const expandedMargin = { ...EXPANDED_CHART_MARGIN, top: 60, right: 30 };
  const expandedDims = getChartDims(true, 460, expandedMargin);

  const getChartAreaClass = () => {
    let className = styles.chartArea;
    if (mobileShowOnly) {
      if (showVolume && mobileShowOnly === "volume") {
        className += " " + chartStyles.chartAreaLeftActive;
      } else if (showVolume && mobileShowOnly === "count") {
        className += " " + chartStyles.chartAreaRightActive;
      } else if (!showVolume && mobileShowOnly === "count") {
        className += " " + chartStyles.chartAreaRightActive;
      } else if (!showVolume && mobileShowOnly === "volume") {
        className += " " + chartStyles.chartAreaLeftActive;
      }
    }
    return className;
  };

  const MainChart: React.FC<{
    data: any[];
    isExpanded?: boolean;
    onExpand?: (which: "volume" | "count") => void;
  }> = ({ data: chartData, isExpanded = false, onExpand }) => {
    const currentDims = isExpanded ? expandedDims : dims;
    let displayData = !isExpanded && previewData ? previewData : chartData;

    if (!isExpanded && previewData && previewHideFinalYear) {
      if (Array.isArray(displayData) && displayData.length > 0) {
        displayData = displayData.slice(0, displayData.length - 1);
      }
    }
    const showHeader = !mobileShowOnly;

    return (
      <div className={getChartAreaClass()}>
        {showHeader && (
          <div className={styles.chartHeaderOverlay}>
            <ChartHeader
              title={headerTitle}
              subtitle={showVolume ? "in CHF Mio." : ""}
              showExpandButton={true}
              onExpand={() => onExpand && onExpand(showVolume ? "volume" : "count")}
            />
          </div>
        )}
        <ResponsiveD3Container width="100%" height={currentDims.height}>
          <D3AreaChart
            data={displayData}
            dataKey={chartKey}
            margin={currentDims.margin}
            strokeColor={chartColor}
            strokeWidth={2}
            fillColor={chartColor}
            fillOpacity={0.8}
            gridColor="#E2E8F0"
            axisColor="#4A5568"
            yTickCount={yTickCount}
            yTickValues={yTickValues}
            onTooltipShow={undefined}
            onTooltipHide={undefined}
            children={null}
          />
        </ResponsiveD3Container>
      </div>
    );
  };

  const ExpandedChart: React.FC<{ data: any[]; controls?: React.ReactNode }> = ({
    data: chartData,
    controls,
  }) => (
    <ExpandedChartLayout
      legendItems={[headerTitle]}
      legendTitle={null}
      showLegend={false}
      colorOf={() => chartColor}
      height={expandedDims.height}
      controls={controls}
    >
      <D3AreaChart
        data={chartData}
        dataKey={chartKey}
        margin={expandedDims.margin}
        strokeColor={chartColor}
        strokeWidth={2}
        fillColor={chartColor}
        fillOpacity={0.8}
        gridColor="#E2E8F0"
        axisColor="#4A5568"
        yTickCount={yTickCount}
        yTickValues={yTickValues}
        onTooltipShow={undefined}
        onTooltipHide={undefined}
        children={null}
      />
    </ExpandedChartLayout>
  );

  return (
    <BaseExpandableChart
      data={data}
      previewData={previewData}
      ChartComponent={({ data, onExpand, children }) => (
        <>
          {children}
          <MainChart data={data} isExpanded={false} onExpand={onExpand} />
        </>
      )}
      ExpandedChartComponent={ExpandedChart}
      supportsTotal={false}
      supportsSingleMode={false}
      isDualChart={false}
      chartTitle={"Investments and Deals"}
      initialSingleMode="line"
    />
  );
};

export default TimelineChart;
